import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import apiRequest from "../../utils/apiRequest";
import RemixModalLoader from "../remix/shared/RemixModalLoader";
import RemixModalComplete from "../remix/shared/RemixModalComplete";
import useActionCable from "./useActionCable";

const AsyncModalRequestConfirmation = (props) => {
  const {
    triggerRef = null,
    triggerSelector = null,
    children = null,
    startCallback = null,
    readyCallback = null,
    redirectPath = null,
    redirectToObjectId = true,
    loadingMessage = 'Loading...',
    endpoint,
    params = {}
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [cableSyncId, setCableSyncId] = useState(null);
  const [activeChannel, setActiveChannel] = useState(null);
  const [progressPercent, setProgressPercent] = useState(null);

  const modalRef = useRef(null);
  const isMounted = useRef(false);
  const pendingRequestRef = useRef(null);

  const { lastMessage: progress } = useActionCable(
    activeChannel,
    { id: cableSyncId },
    {
      autoDisconnect: true,
      autoDisconnectCondition: (value) => value === 100,
    }
  );

  useActionCable(
    isLoading ? "AsyncModalRequestConfirmationChannel" : null,
    { id: cableSyncId },
    {
      onConnected: () => {
        // Fire the request only once the channel is subscribed, so the server
        // can never broadcast "ready" before we're listening for it.
        if (pendingRequestRef.current) {
          const { endpoint: requestEndpoint, params: requestBody } = pendingRequestRef.current;
          pendingRequestRef.current = null;
          apiRequest("POST", requestEndpoint, requestBody);
        }
      },
      onReceived: (data) => {
        switch (data?.status) {
          case "ready":
            handleSuccess(data);
            break;
          case "failed":
            console.error('AsyncModalRequestConfirmation - Request failed on server:', data.error);
            setErrorMessage(data?.error || 'Something went wrong while duplicating. Please try again.');
            setIsLoading(false);
            break;
          case "progress":
            if (typeof data.percent === "number") setProgressPercent(data.percent);
            if (data.label) setStatusMessage(data.label);
            break;
          default:
            console.warn('AsyncModalRequestConfirmation - Unknown status received:', data);
        }
      },
      autoDisconnect: true,
      autoDisconnectCondition: (data) => data?.status === "ready" || data?.status === "failed",
    }
  );

  useEffect(() => {
    isMounted.current = true;

    const handleTriggerClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      handleConfirmation(event);
    };

    let triggerElements = [];

    if (triggerRef) {
      triggerElements = [triggerRef.current];
    }

    if (triggerSelector) {
      triggerElements = document.querySelectorAll(triggerSelector);
    }

    if (triggerElements.length > 0) {
      triggerElements.forEach(element => {
        element.addEventListener('click', handleTriggerClick);
        element.addEventListener('submit', handleTriggerClick);
      });

      return () => {
        triggerElements.forEach(element => {
          element.removeEventListener('click', handleTriggerClick);
          element.removeEventListener('submit', handleTriggerClick);
        });
      };
    }

    return () => {
      isMounted.current = false;
    };
  }, [triggerSelector, triggerRef]);

  const showModal = () => {
    // Close all open modals except the AsyncModalRequestConfirmation modal
    if (window.$ && window.$.fn.modal) {
      window.$('.modal').each(function() {
        const modal = window.$(this);
        // Don't close the AsyncModalRequestConfirmation modal
        if (modal.attr('id') !== 'asyncDuplicateStatusModal' && modal.data('bs.modal')) {
          modal.modal('hide');
        }
      });
    }

    setIsModalOpen(true);
    if (modalRef.current) {
      if (window.$ && window.$.fn.modal) {
        window.$(modalRef.current).modal('show');
      }
    }
  };

  const hideModal = () => {
    if (modalRef.current && window.$ && window.$.fn.modal) {
      try {
        window.$(modalRef.current).modal('hide');
      } catch (e) {
        // no-op: modal was never Bootstrap-managed
      }
    }
    if (isMounted.current) {
      setIsModalOpen(false);
    }
  };

  const setLoadingState = (canRemix = false, hasTemplate = false) => {
    setIsLoading(true);
    setErrorMessage(null);
    setRedirectUrl(null);
    let message = loadingMessage;
    if (canRemix && hasTemplate) {
      message = 'Remixing template...';
    }
    setStatusMessage(message);
  };

  const handleConfirmation = async (event) => {
    let requestParams = { ...params };
    let canRemix = false;
    let hasTemplate = false;

    if (Object.keys(requestParams).length === 0 && event.target) {
      const formElement = event.target.closest('form');

      if (formElement) {
        const formData = new FormData(formElement);
        for (const [key, value] of formData.entries()) {
          if (key.includes('[')) {
            const match = key.match(/^([^\[]+)\[([^\]]+)\]$/);
            if (match) {
              const [_, parentKey, childKey] = match;
              if (!requestParams[parentKey]) requestParams[parentKey] = {};
              requestParams[parentKey][childKey] = value;
              
              // Check if this is the can_remix field
              if (parentKey === 'remix' && childKey === 'can_remix' && value === 'true') {
                canRemix = true;
              }
              
              // Check if this is a template field (e.g., cta[template])
              if (childKey === 'template' && value && value.trim() !== '') {
                hasTemplate = true;
              }
            } else {
              requestParams[key] = value;
            }
          } else {
            requestParams[key] = value;

            // Check if template_id or template has a value
            if ((key === 'template_id' || key === 'template') && value && value.trim() !== '') {
              hasTemplate = true;
            }
          }
        }

        // Parse excluded_element_types from JSON string or comma-separated to array
        if (requestParams.remix) {
          const raw = requestParams.remix.excluded_element_types;
          if (Array.isArray(raw)) {
            requestParams.remix.excluded_element_types = raw;
          } else if (raw && typeof raw === "string") {
            const val = raw.trim();
            if (val.startsWith("[")) {
              try {
                const parsed = JSON.parse(val);
                requestParams.remix.excluded_element_types = Array.isArray(
                  parsed
                )
                  ? parsed
                  : [];
              } catch {
                requestParams.remix.excluded_element_types = [];
              }
            } else {
              requestParams.remix.excluded_element_types = val
                ? val.split(",").map((s) => s.trim()).filter(Boolean)
                : [];
            }
          } else {
            requestParams.remix.excluded_element_types = [];
          }
        }
      }
    } else {
      if (requestParams.remix && requestParams.remix.can_remix === "true") {
        canRemix = true;
      }
      if ((requestParams.template_id && requestParams.template_id.trim() !== '') ||
          (requestParams.template && requestParams.template.trim() !== '') ||
          (requestParams.cta && requestParams.cta.template && requestParams.cta.template.trim() !== '')) {
        hasTemplate = true;
      }
    }



    if (isLoading) {
      return;
    }

    if (startCallback && typeof startCallback === 'function') {
      startCallback();
    }

    if (!isMounted.current) return;

    try {
      const newSyncId = Math.random().toString(36).substring(2, 15);
      pendingRequestRef.current = {
        endpoint,
        params: { ...requestParams, cable_sync_id: newSyncId },
      };
      setCableSyncId(newSyncId);
      setActiveChannel("ProgressValueChannel");

      showModal();
      setLoadingState(canRemix, hasTemplate);
    } catch (error) {
      console.error('Error starting duplication:', error);
    }
  };

  const handleSuccess = (data) => {
    // readyCallback may return the URL it wants us to navigate to, so the modal
    // can show the "complete" state and own the redirect.
    let callbackRedirectUrl = null;
    if (readyCallback && typeof readyCallback === 'function') {
      callbackRedirectUrl = readyCallback(data);
    }

    if (!isMounted.current) return;

    setIsLoading(false);

    let redirectUrl = null;

    if (redirectPath) {
      const objectId = data?.object?.id;
      if (redirectToObjectId && (objectId === undefined || objectId === null)) {
        console.error(
          'AsyncModalRequestConfirmation - Cannot redirect: object id is missing',
          data
        );
        setActiveChannel(null);
        setCableSyncId(null);
        setErrorMessage(
          'The resource was created but could not redirect. Please try again or navigate manually from your campaigns list.'
        );
        return;
      }

      redirectUrl = redirectPath;
      if (redirectToObjectId) {
        redirectUrl = redirectUrl.endsWith('/') ? `${redirectUrl}${objectId}` : `${redirectUrl}/${objectId}`;
      }

      if (['ctas', 'broadcasts', 'templates'].includes(data.object_type)) {
        redirectUrl += `/edit`;
      }

      if (['variants'].includes(data.object_type)) {
        redirectUrl += `/variants/${data.object.id}/edit`;
      }
    } else if (typeof callbackRedirectUrl === 'string' && callbackRedirectUrl) {
      redirectUrl = callbackRedirectUrl;
    }

    if (redirectUrl) {
      // Render the "complete" state and redirect immediately. The card is only
      // feedback for the gap until navigation happens; the link is the fallback.
      setRedirectUrl(redirectUrl);
      window.location.href = redirectUrl;
    } else {
      setTimeout(() => {
        if (!isMounted.current) return;
        resetState();
      }, 1000);
    }
  };

  const handleChildrenClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleConfirmation(event);
  };

  const resetState = () => {
    pendingRequestRef.current = null;
    setIsLoading(false);
    setStatusMessage('');
    setProgressPercent(null);
    setErrorMessage(null);
    setRedirectUrl(null);
    setActiveChannel(null);
    setCableSyncId(null);
    hideModal();
  };

  return (
    <>
      {children && (
        <div onClick={handleChildrenClick}>
          {children}
        </div>
      )}

      {isModalOpen && createPortal(
        <div
          ref={modalRef}
          id="asyncDuplicateStatusModal"
          className="modal"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="asyncDuplicateObjectlLabel"
          style={{ display: 'block' }}
        >
          <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <div className="modal-dialog rounded-2xl" role="document" style={{ maxWidth: '500px' }}>
              <div className="modal-content smooth">
                <div className="modal-body">
                  <button
                    type="button"
                    className="close"
                    onClick={resetState}
                    aria-label="Close"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                      <path d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>

                  {errorMessage ? (
                    <div className="row hard-center p-4 center" style={{ marginTop: '30px', marginBottom: '40px' }}>
                      <div
                        className="col-xs-12 p-4 rounded-md bg-red-50 border border-red-200 text-red-800"
                        role="alert"
                      >
                        <h4 className="font-semibold mb-2">Something went wrong</h4>
                        <p>{errorMessage}</p>
                      </div>
                    </div>
                  ) : redirectUrl ? (
                    <RemixModalComplete redirectUrl={redirectUrl} />
                  ) : isLoading ? (
                    <RemixModalLoader
                      statusMessage={statusMessage}
                      progress={progressPercent ?? progress}
                      showAiGeneratingStep={false}
                      showSteps={false}
                      bottomMessage="This may take a moment."
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </>
  );
};

export default AsyncModalRequestConfirmation;
